/**
 * @fileoverview Recursive row for the assessed curriculum hierarchy (lv2 → lv4).
 * Indentation comes from nesting: each branch wraps its children in a box that
 * shifts right and draws a vertical guide line (borderLeft), mirroring the
 * design's tree connectors. Level-2 rows carry a full-width subtle band.
 * Branches (lv2/lv3) expand/collapse; leaves (lv4) show the average grade as a
 * hand icon. Only assessed branches/leaves reach this component (see tree.ts).
 */

import { useState } from 'react'
import { Box, Collapse, Group, Text, UnstyledButton } from '@mantine/core'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import { AssessmentScore } from './AssessmentScore'
import type { ReportNode } from '../types'

interface CoverageRowProps {
  readonly node: ReportNode
  readonly depth: number
  readonly scoreTooltip: string
}

const CHEVRON_SIZE = 16
const ROW_PADDING_Y = 8
const ROW_PADDING_LEFT = 12
// Indent the children box so its guide line falls on the parent chevron's
// center. The same relative padding repeats at every level, so lining up the
// line with the chevron once keeps it aligned all the way down.
const GUIDE_OFFSET = ROW_PADDING_LEFT + CHEVRON_SIZE / 2
const GUIDE_COLOR = 'var(--mantine-color-gray-3, #dee2e6)'
const LV2_BAND_BG = 'var(--mantine-color-gray-0, #f8f9fa)'
const LV2_DEPTH = 1

export function CoverageRow({ node, depth, scoreTooltip }: CoverageRowProps) {
  const isBranch = node.children.length > 0
  const [open, setOpen] = useState(true)

  if (!isBranch) {
    return (
      <Group position='apart' align='flex-start' noWrap py={ROW_PADDING_Y} pl={ROW_PADDING_LEFT} pr='sm'>
        <Text size='sm' style={{ flex: 1 }}>
          {node.originalCodes && <strong>{node.originalCodes} — </strong>}
          {node.name}
        </Text>
        <AssessmentScore score={node.average} tooltip={scoreTooltip} />
      </Group>
    )
  }

  const banded = depth === LV2_DEPTH

  return (
    <Box>
      <UnstyledButton
        onClick={() => setOpen(value => !value)}
        py={ROW_PADDING_Y}
        pl={ROW_PADDING_LEFT}
        pr='sm'
        style={{ width: '100%', backgroundColor: banded ? LV2_BAND_BG : undefined }}
      >
        <Group spacing={6} align='center' noWrap>
          {open ? <IconChevronDown size={CHEVRON_SIZE} /> : <IconChevronRight size={CHEVRON_SIZE} />}
          <Text size='sm' weight={500}>
            {node.name}
          </Text>
        </Group>
      </UnstyledButton>
      <Collapse in={open}>
        <Box ml={GUIDE_OFFSET} sx={{ borderLeft: `1px solid ${GUIDE_COLOR}` }}>
          {node.children.map(child => (
            <CoverageRow key={child.id} node={child} depth={depth + 1} scoreTooltip={scoreTooltip} />
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}
